/**
 * What the other banks show in public (chair DESIGN §5.3): the public measures of `MarketView.banks` as rows of You, peer median and rank, one column per other bank,
 * and the rate sheets. Pure; the table is `theme/PeerTable`. Only `PUBLIC_KPIS` are carried by the contract, so a measure outside them has no peer figure and no row here.
 */
import { PUBLIC_KPIS, PUBLIC_KPIS_EXTENDED, type KpiId, type KpiSnapshot } from "@bsim/domain";
import type { MarketView } from "@bsim/contracts";
import { formatKpi, formatRate, ordinal } from "../format";
import { LOWER_IS_BETTER, kpiLabel } from "../format/labels";
import type { PeerRow } from "../theme/PeerTable";

type Bank = MarketView["banks"][number];

/** Every measure a bank publishes: the headline set and the further ones its filings give (ADR-0076, `kpisExtended`). */
export const PEER_KPIS: readonly KpiId[] = [...PUBLIC_KPIS, ...PUBLIC_KPIS_EXTENDED];
const isPublic = (id: KpiId): boolean => PEER_KPIS.includes(id);
const kpiOf = (b: Bank, id: KpiId): number | undefined => b.kpis[id] ?? b.kpisExtended?.[id];
/** Lower is better for these; loans to deposits has no better side, so its rank cell says so. */
const LOWER: ReadonlySet<KpiId> = new Set<KpiId>([...LOWER_IS_BETTER, "costOfDeposits", "creToTrbc"]);
/** The public measures in the order a reader meets them; the same groups head the Peers table and sit, in this order, across the drawer's tabs. */
export const PEER_GROUPS: readonly (readonly [name: string, ids: readonly KpiId[]])[] = [
  ["Prices and value", ["price", "ptbv", "tbvps", "dps"]],
  ["Earnings", ["rotce", "roa", "nim", "efficiencyRatio", "customerIndex"]],
  ["Capital", ["cet1Reported", "trbc", "creToTrbc"]],
  ["Liquidity", ["lcrLite", "ldr", "uninsuredShare", "costOfDeposits", "ddaShare"]],
  ["Credit", ["ncoRate", "nplRate", "aclRatio"]],
  ["Growth", ["loanGrowth", "depositGrowth"]],
];
const NO_RANK: ReadonlySet<KpiId> = new Set<KpiId>(["ldr"]);

/** `peerRows` for the groups named, in group order, each row tagged with its group (the table prints a header row where the group changes). */
export function groupedPeerRows(market: MarketView, mySeatId: string, groups: readonly string[]): readonly PeerRow[] {
  return PEER_GROUPS.filter(([name]) => groups.includes(name)).flatMap(([name, ids]) => peerRows(market, mySeatId, ids).map((r): PeerRow => ({ ...r, group: name })));
}

/** The other banks, by share price (highest first): the order of their columns. */
export const othersOf = (market: MarketView, mySeatId: string): readonly Bank[] =>
  market.banks.filter((b) => b.seatId !== mySeatId).sort((a, b) => (b.kpis.price ?? 0) - (a.kpis.price ?? 0) || a.name.localeCompare(b.name));

/** A gap between peers exists only once a round has been released: before it every bank is at the same SimStart position, so the median is the player's own figure. */
export const peersInformative = (market: MarketView): boolean => market.throughRoundNo >= 1;

const median = (values: readonly number[]): number => {
  const all = [...values].sort((a, b) => a - b);
  return all.length % 2 === 1 ? (all[(all.length - 1) / 2] ?? 0) : ((all[all.length / 2 - 1] ?? 0) + (all[all.length / 2] ?? 0)) / 2;
};

/** "3rd of 6"; "level" when every other bank prints the same figure as yours; "tied 3rd of 6" when some do. Ties are read on the printed figure. */
export function rankText(id: KpiId, mine: number, others: readonly number[]): string {
  const printed = (v: number): string => formatKpi(id, v);
  const same = others.filter((v) => printed(v) === printed(mine)).length;
  if (same === others.length) return "level";
  const better = others.filter((v) => printed(v) !== printed(mine) && (LOWER.has(id) ? v < mine : v > mine)).length;
  return `${same > 0 ? "tied " : ""}${ordinal(better + 1)} of ${others.length + 1}`;
}

/**
 * One row per public measure in `ids` that the player and every other bank report: your figure (`own` when given, else the market's), the median of all banks, your rank, and each other bank's figure
 * in `othersOf` order. `level` marks a row every bank prints the same.
 */
export function peerRows(market: MarketView, mySeatId: string, ids: readonly KpiId[], own?: KpiSnapshot): readonly PeerRow[] {
  const me = market.banks.find((b) => b.seatId === mySeatId);
  const others = othersOf(market, mySeatId);
  return ids.filter(isPublic).flatMap((id): PeerRow[] => {
    const mine = own?.[id] ?? (me === undefined ? undefined : kpiOf(me, id));
    const theirs = others.map((b) => kpiOf(b, id));
    if (mine === undefined || others.length === 0 || theirs.some((v) => v === undefined)) return [];
    const values = theirs.filter((v): v is number => v !== undefined);
    const printed = new Set([...values, mine].map((v) => formatKpi(id, v)));
    return [{ id, label: kpiLabel(id), term: id, you: formatKpi(id, mine), median: formatKpi(id, median([...values, mine])), rank: NO_RANK.has(id) ? "not ranked" : rankText(id, mine, values), banks: values.map((v) => formatKpi(id, v)), level: printed.size === 1 }];
  });
}

/** The loan book or the deposit book as shares by product, one row per product any bank holds (a bank without it holds none): your share, the median and each other bank's, in the Books page's own product names. */
export function mixRows(market: MarketView, mySeatId: string, book: "loans" | "deposits"): readonly PeerRow[] {
  const me = market.banks.find((b) => b.seatId === mySeatId);
  const others = othersOf(market, mySeatId);
  if (me?.mix === undefined || others.some((b) => b.mix === undefined)) return [];
  const labels = new Map<string, string>();
  for (const b of [me, ...others]) for (const l of b.mix?.[book] ?? []) if (!labels.has(l.productId)) labels.set(l.productId, l.label);
  const share = (b: Bank, id: string): number => Number(b.mix?.[book].find((l) => l.productId === id)?.shareRate ?? 0);
  const text = (v: number): string => formatRate(v, 1);
  return [...labels].map(([id, label]): PeerRow => {
    const [mine, theirs] = [share(me, id), others.map((b) => share(b, id))];
    return { id, label, you: text(mine), median: text(median([...theirs, mine])), rank: null, banks: theirs.map(text), level: new Set([mine, ...theirs].map(text)).size === 1 };
  });
}

/** Rate sheet product keys as plain words ("mmda_retail" is "Money market, retail"): one word at a time, the acronyms spelled the way a banker says them. */
const WORDS: Readonly<Record<string, string>> = {
  ci: "Business loans", cre: "Commercial property loans", mbs: "MBS", ust: "Treasuries", fhlb: "FHLB", cd: "CD", dda: "Non-interest checking", mmda: "Money market", int: "Interest", afs: "AFS", htm: "HTM", ipre: "income property",
  hvcre: "high-volatility commercial property", resi: "Residential", gnma: "Ginnie Mae", dw: "Discount window", ffp: "Fed funds purchased", sub: "Subordinated", fhlb_short: "FHLB, short",
};
export const productWords = (key: string): string => {
  const words = key.split("_").map((w) => WORDS[w] ?? w);
  const text = words.join(" ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** The rate sheets as rows (`names`: the products' own names, the Books page's; a key without one is spelled out): one per product that every bank posts, your rate, the median, the spread to it and each other bank's rate. */
export function rateSheetRows(market: MarketView, mySeatId: string, names?: ReadonlyMap<string, string>): readonly PeerRow[] {
  const me = market.banks.find((b) => b.seatId === mySeatId);
  const others = othersOf(market, mySeatId);
  if (me === undefined || others.length === 0) return [];
  // The products' own order (loans, deposits, funding as the Books page has them) when the names are given; any other key follows, by name.
  const order = [...(names?.keys() ?? [])];
  const at = (key: string): number => { const i = order.indexOf(key); return i < 0 ? order.length : i; };
  return Object.keys(me.rateSheet).sort((a, b) => at(a) - at(b) || a.localeCompare(b)).flatMap((key): PeerRow[] => {
    const mine = me.rateSheet[key];
    const theirs = others.map((b) => b.rateSheet[key]);
    if (mine === undefined || theirs.some((v) => v === undefined)) return [];
    const values = theirs.flatMap((v) => (v === undefined ? [] : [Number(v)]));
    const text = (v: number): string => formatRate(v, 2);
    return [{ id: key, label: names?.get(key) ?? productWords(key), you: text(Number(mine)), median: text(median([...values, Number(mine)])), rank: null, banks: values.map(text), level: new Set([...values, Number(mine)].map(text)).size === 1 }];
  });
}
